class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectInterval = 3000; // 3 secondes
    this.listeners = new Map();
    this.isConnecting = false;
  } 

  connect() {
    if (this.socket?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;
    // URL depuis variable d'environnement uniquement (NO HARDCODING)
    const wsUrl = process.env.REACT_APP_WS_URL;

    if (!wsUrl) {
      console.error('❌ WebSocket - REACT_APP_WS_URL non définie dans .env');
      this.isConnecting = false;
      return;
    }

    console.log('🔌 WebSocket - Tentative de connexion :', wsUrl);

    try {
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('🔌 WebSocket - Socket ouvert, attente confirmation serveur testing ...');
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        // Configurer le ping périodique (toutes les 30 secondes)
        this.pingInterval = setInterval(() => {
          if (this.socket?.readyState === WebSocket.OPEN) {
            this.sendPing();
          }
        }, 30000);
      };

      this.socket.onmessage = (event) => {
        try {
          // Gérer les messages pong qui ne sont pas JSON
          if (event.data === 'pong') {
            console.log('🏓 Pong reçu');
            return;
          }

          const data = JSON.parse(event.data);

          // Gérer la confirmation de connexion immédiate
          if (data.type === 'connection_confirmed') {
            console.log('✅ WebSocket - Connexion confirmée par le serveur! Prêt à recevoir les événements');
            return;
          }

          console.log('📨 Message WebSocket reçu:', data);

          // Notifier tous les listeners (chaque type peut avoir plusieurs callbacks)
          this.listeners.forEach((callbacks, type) => {
            if (type === 'all' || data.data_type === type) {
              console.log(`📢 Appel de ${callbacks.length} callback(s) pour type: ${type}`);
              callbacks.forEach(callback => {
                try {
                  callback(data);
                } catch (error) {
                  console.error(`❌ Erreur dans callback pour ${type}:`, error);
                }
              });
            }
          });
        } catch (error) {
          console.error('❌ Erreur parsing message WebSocket:', error);
        }
      };

      this.socket.onclose = (event) => {
        console.log('❌ WebSocket - Connexion fermée:', { code: event.code, reason: event.reason });
        this.isConnecting = false;

        if (this.pingInterval) {
          clearInterval(this.pingInterval);
        }

        // Tentative de reconnexion automatique
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`🔄 WebSocket - Reconnexion ${this.reconnectAttempts}/${this.maxReconnectAttempts} dans ${this.reconnectInterval}ms`);

          setTimeout(() => {
            this.connect();
          }, this.reconnectInterval);
        } else {
          console.error('❌ WebSocket - Connexion définitivement échouée après', this.maxReconnectAttempts, 'tentatives');
        }
      };

      this.socket.onerror = (error) => {
        console.error('❌ WebSocket - Erreur de connexion:', error);
        this.isConnecting = false;
      };

    } catch (error) {
      console.error('❌ WebSocket - Erreur lors de la création:', error);
      this.isConnecting = false;
    }
  }

  disconnect() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.listeners.clear();
    console.log('🔌 WebSocket - Déconnexion manuelle');
  }

  sendPing() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send('ping');
    }
  }

  // Écouter les changements de données
  onDataChange(dataType, callback) {
    const key = dataType || 'all';

    // Récupérer l'array existant ou créer un nouveau
    if (!this.listeners.has(key)) {
      this.listeners.set(key, []);
    }

    const callbacks = this.listeners.get(key);

    // Éviter les doublons (même callback ajouté 2 fois)
    if (!callbacks.includes(callback)) {
      callbacks.push(callback);
      console.log(`📡 Listener ajouté pour: ${dataType || 'all'} (total: ${callbacks.length})`);
    } else {
      console.log(`⚠️ Listener déjà existant pour: ${dataType || 'all'}`);
    }
  }

  // Arrêter d'écouter les changements
  offDataChange(dataType, callback) {
    const key = dataType || 'all';

    if (!this.listeners.has(key)) {
      return; // Pas de listeners pour ce type
    }

    const callbacks = this.listeners.get(key);

    // Retirer le callback spécifique de l'array
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
      console.log(`🗑️ Listener retiré pour: ${dataType || 'all'} (restant: ${callbacks.length})`);
    }

    // Si l'array est vide, supprimer la clé
    if (callbacks.length === 0) {
      this.listeners.delete(key);
      console.log(`🧹 Aucun listener restant pour: ${dataType || 'all'}, clé supprimée`);
    }
  }

  // Vérifier l'état de la connexion
  isConnected() {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  // Réinitialiser et reconnecter
  reconnect() {
    this.disconnect();
    this.reconnectAttempts = 0;
    setTimeout(() => this.connect(), 1000);
  }
}

// Instance singleton
const websocketService = new WebSocketService();

export default websocketService;